import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Clock as ClockIcon, X } from "lucide-react";
import toast from "react-hot-toast";
import { attendanceService, authService } from "../../services/api";
import {
  evaluateCheckInPunch,
  evaluateDayAttendance,
  isEmployeeManualAttendanceProcess,
} from "../../lib/attendancePolicyEngine";
import { resolveShiftNameHintForDay } from "../../lib/attendanceShiftOverride";
import {
  ATTENDANCE_PUNCH_UPDATED_EVENT,
  LEGACY_CHECKIN_KEY,
  getLocalDayKey,
  formatDurationHms,
  workedMsFromPunchRows,
} from "../../lib/attendancePunchStorage";
import {
  getAuthJson,
  setAuthJson,
  getAuthItem,
  setAuthItem,
  isEssProxySession,
} from "../../utils/authStorage";
import { ATTENDANCE_POLICY_UPDATED_EVENT } from "../../lib/attendanceDemoSeed";
import { syncAttendancePoliciesCache } from "../../lib/syncAttendancePoliciesCache";
import { ATTENDANCE_SCHEDULE_RECALC_EVENT } from "../../lib/attendanceGeneralSettings";
import { useAttendanceCodeConfig } from "../../context/attendanceCodeConfigContext";
import { formatCompositeStatusLabel } from "../../lib/attendancePolicyConfig";
import {
  LOCATION_REQUIRED_MESSAGE,
  resolvePunchLocationPayload,
} from "../../lib/punchLocation";
import { resolveAttendanceDisplayTimeZone } from "../../lib/attendanceDayChecks";
import { socket } from "../../services/websocket";

export const ADMIN_HOME_CHECKIN_KEY = LEGACY_CHECKIN_KEY;

function authEmployeeId() {
  const fromItem = getAuthItem("employeeId");
  if (fromItem) return String(fromItem);
  const u = getAuthJson("currentUser", null);
  const id = u?.employeeId ?? u?.employee?.id ?? null;
  return id != null && id !== "" ? String(id) : null;
}

async function fetchMyHistoryRowsForLivePunch() {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  const hist = await attendanceService.getMyAttendanceHistory(month, year);
  let rows = Array.isArray(hist?.data) ? hist.data : [];
  if (now.getDate() === 1) {
    const prev = new Date(year, now.getMonth() - 1, 1);
    const prevHist = await attendanceService.getMyAttendanceHistory(
      prev.getMonth() + 1,
      prev.getFullYear()
    );
    rows = [...(Array.isArray(prevHist?.data) ? prevHist.data : []), ...rows];
  }
  const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
  if (now.getDate() >= lastDay) {
    const next = new Date(year, now.getMonth() + 1, 1);
    const nextHist = await attendanceService.getMyAttendanceHistory(
      next.getMonth() + 1,
      next.getFullYear()
    );
    rows = [...rows, ...(Array.isArray(nextHist?.data) ? nextHist.data : [])];
  }
  return rows;
}

function isSameLocalDay(aMs, bMs) {
  const a = new Date(aMs);
  const b = new Date(bMs);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Latest unmatched IN. Navbar Work Hours runs from this punch, not First IN. */
function openSessionStartMs(session) {
  const list = Array.isArray(session?.sessions) ? session.sessions : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const row = list[i];
    if (!row || row.outMs != null) continue;
    const n = Number(row.inMs);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

function formatCheckInClock(ms) {
  if (ms == null || !Number.isFinite(ms)) return "—";
  const timeZone = resolveAttendanceDisplayTimeZone();
  return new Date(ms).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  });
}

function computeLiveDayStatus({ employeeId, checkInAt, checkOutAt, openSession }) {
  if (!employeeId || checkInAt == null) return "-";
  const dayKey = getLocalDayKey(checkInAt);
  const shiftNameHint = resolveShiftNameHintForDay(employeeId, dayKey);
  const firstIn = checkInAt;
  const lastOut = openSession ? null : checkOutAt ?? null;
  const workedHoursOverride = openSession && checkInAt != null
    ? (Date.now() - checkInAt) / 3600000
    : (checkInAt != null && checkOutAt != null ? (checkOutAt - checkInAt) / 3600000 : 0);
  const result = evaluateDayAttendance({
    employee: { id: employeeId, employeeId },
    date: new Date(firstIn),
    checkIn: firstIn,
    checkOut: lastOut,
    shiftNameHint: shiftNameHint || null,
    isToday: true,
    openSession: Boolean(openSession),
    workedHoursOverride,
  });
  return result?.status || "-";
}

/**
 * Navbar check-in / check-out for the logged-in user's employee record.
 * Persists punches locally and via attendance API; notifies calendar to recalc PR/LOP.
 * Hidden when the employee's policy has Auto attendance + Override Attendance.
 */
export default function AdminCheckInOut({ darkMode, variant = "navbar" }) {
  const { mapping: attendanceMapping } = useAttendanceCodeConfig();
  const [employeeId, setEmployeeId] = useState(() => authEmployeeId());
  const [apiReady, setApiReady] = useState(false);
  const [punchBusy, setPunchBusy] = useState(false);
  const [manualProcess, setManualProcess] = useState(false);

  useEffect(() => {
    const sync = () => {
      if (employeeId) {
        const emp = { id: employeeId, employeeId };
        if (isEmployeeManualAttendanceProcess(emp)) {
          setManualProcess(true);
          return;
        }
        setManualProcess(false);
        try {
          const gate = evaluateCheckInPunch({
            employee: emp,
            checkInMs: Date.now(),
            channel: "web",
          });
          // Hide navbar punch when web channel is off (policy).
          if (gate?.policy && gate.policy.checkInWeb === false) {
            setManualProcess(true);
          }
        } catch {
          /* keep visible */
        }
      } else {
        setManualProcess(false);
      }
    };
    sync();
    window.addEventListener(ATTENDANCE_POLICY_UPDATED_EVENT, sync);
    return () => window.removeEventListener(ATTENDANCE_POLICY_UPDATED_EVENT, sync);
  }, [employeeId]);

  const applyServerSession = useCallback((session) => {
  if (!session) {
    setCheckedIn(false);
    setCheckInAt(null);
    setCheckOutAt(null);
    setOpenInAt(null);
    setDayPunches([]);
    setLastSession(null);
    setShowWorkHours(false);
    return;
  }

  const open =
    session.open === true ||
    Boolean(
      session.checkIn &&
      !session.checkOut
    );

  setCheckedIn(open);

  setCheckInAt(
    session.checkIn
      ? new Date(
          session.checkIn
        ).getTime()
      : null
  );

  setCheckOutAt(
    open
      ? null
      : session.checkOut
        ? new Date(
            session.checkOut
          ).getTime()
        : null
  );

  setLastSession(
    session.lastSession || null
  );
  setOpenInAt(
    open
      ? openSessionStartMs(session) ||
          (session.checkIn ? new Date(session.checkIn).getTime() : null)
      : null
  );
  setDayPunches(Array.isArray(session.punches) ? session.punches : []);
  setShowWorkHours(session.showWorkHours === true);
  if (session.status) {
    setLiveStatus(session.status);
    lastSyncedStatusRef.current = session.status;
  }
}, []);

  const hydrateLiveFromServer = useCallback(async (sid) => {
    if (!sid) return false;
    try {
      // Just refresh from current session API - no need to hydrate localStorage
      const res = await attendanceService.getCurrentAttendanceSession();
      if (res?.success) {
        applyServerSession(
          res.data
        );
      }
      return true;
    } catch {
      return false;
    }
  }, [applyServerSession]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Prefer session identity immediately (Access ESS must not wait on API
      // while briefly reading the admin's shared legacy punch keys).
      const seeded = authEmployeeId();
      if (!cancelled && seeded) setEmployeeId(seeded);

      try {
        const res = await authService.getCurrentUser();
        const id = res?.data?.employeeId ?? res?.data?.employee?.id ?? seeded ?? null;
        if (!cancelled && id != null) {
          const sid = String(id);
          setEmployeeId(sid);
          
          try {
            const u = getAuthJson("currentUser", null);
            if (u && typeof u === "object" && !u.employeeId) {
              setAuthJson("currentUser", { ...u, employeeId: id });
            }
            if (!getAuthItem("employeeId")) {
              setAuthItem("employeeId", sid);
            }
          } catch {
            /* ignore */
          }
          await syncAttendancePoliciesCache();
          // Login never calls check-in. Navbar state comes from the server
          // open session (and other devices via socket/focus refresh below).
          await hydrateLiveFromServer(sid);
        }
      } catch {
        /* offline / no employee link — keep seeded id */
      } finally {
        if (!cancelled) setApiReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrateLiveFromServer]);

  // Do not paint Check Out from leftover localStorage / adminHomeCheckInAt
  // before the server hydrate. That looked like an automatic check-in on login.
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkInAt, setCheckInAt] = useState(null);
  const [checkOutAt, setCheckOutAt] = useState(null);
  const [openInAt, setOpenInAt] = useState(null);
  const [dayPunches, setDayPunches] = useState([]);
  const [showWorkHours, setShowWorkHours] = useState(false);

  const [lastSession, setLastSession] = useState(null);
  const [tick, setTick] = useState(0);
  const [liveStatus, setLiveStatus] = useState("-");
  const lastSyncedStatusRef = useRef(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);

  const isCompact = variant === "navbar";

  useEffect(() => {
    if (!apiReady || !employeeId) {
      if (apiReady && !employeeId) {
        setCheckedIn(false);
        setCheckInAt(null);
        setCheckOutAt(null);
        setOpenInAt(null);
        setDayPunches([]);
        setLastSession(null);
        setShowWorkHours(false);
      }
      return;
    }

    // Backend is the source of truth for attendance state
    // Fetch current session state from server
    const fetchCurrentSession = async () => {
      try {
        const res = await attendanceService.getCurrentAttendanceSession();
        if (res?.success) {
          const session = res.data;
          applyServerSession(session);
        }
      } catch (err) {
        // If API fails, default to no session
        setCheckedIn(false);
        setCheckInAt(null);
        setCheckOutAt(null);
        setOpenInAt(null);
        setDayPunches([]);
        setShowWorkHours(false);
      }
    };

    fetchCurrentSession();
  }, [apiReady, employeeId, applyServerSession]);

  // Keep navbar in sync when calendar / other tabs update the same worklog.
  useEffect(() => {
    if (!employeeId) return;
    const onPunch = (evt) => {
      const detailId = evt?.detail?.employeeId;
      if (detailId != null && String(detailId) !== String(employeeId)) return;
      // Refresh from server instead of localStorage
      attendanceService.getCurrentAttendanceSession()
        .then(res => {
          if (res?.success) {
            const session = res.data;
            applyServerSession(session);
          }
        })
        .catch(() => {});
    };
    window.addEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
    return () => window.removeEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
  }, [employeeId, applyServerSession]);

  // Other laptops / Access ESS / employee login: apply the same server session.
  useEffect(() => {
    if (!apiReady || !employeeId) return;
    const selfId = String(employeeId);
    const refresh = () => {
      // Refresh from server instead of localStorage
      attendanceService.getCurrentAttendanceSession()
        .then(res => {
          if (res?.success) {
            const session = res.data;
            applyServerSession(session);
          }
        })
        .catch(() => {
          // On error, don't change state
        });
    };
    const onSocket = (payload) => {
      const punchEmp = String(payload?.employeeId ?? payload?.employee_id ?? "");
      if (!punchEmp || punchEmp !== selfId) return;
      window.setTimeout(refresh, 250);
    };
    const onFocusOrVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      refresh();
    };
    if (socket?.on) {
      socket.on("attendance-updated", onSocket);
      socket.on("attendance-fast-updated", onSocket);
    }
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);
    return () => {
      if (socket?.off) {
        socket.off("attendance-updated", onSocket);
        socket.off("attendance-fast-updated", onSocket);
      }
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
  }, [apiReady, employeeId]);

  const updateDropdownPosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useEffect(() => {
    if (!checkedIn || (checkInAt == null && openInAt == null)) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [checkedIn, checkInAt, openInAt]);

  // Calculate work hours based on server state, not localStorage
  // Same sum as Day details Total hours: closed IN→OUT pairs, plus open IN→now.
  const nowMs = Date.now();
  const punchDayKey = dayPunches.length
    ? getLocalDayKey(new Date(dayPunches[0].punchedAt || dayPunches[0].punched_at).getTime())
    : null;
  const firstInMs = dayPunches.reduce((earliest, row) => {
    const kind = String(row.punchKind || row.punch_kind || "").toLowerCase();
    if (kind === "out") return earliest;
    const ms = new Date(row.punchedAt || row.punched_at).getTime();
    if (!Number.isFinite(ms)) return earliest;
    return earliest == null || ms < earliest ? ms : earliest;
  }, null);
  const lastOutMs = dayPunches.reduce((latest, row) => {
    const kind = String(row.punchKind || row.punch_kind || "").toLowerCase();
    if (kind !== "out") return latest;
    const ms = new Date(row.punchedAt || row.punched_at).getTime();
    if (!Number.isFinite(ms)) return latest;
    return latest == null || ms > latest ? ms : latest;
  }, null);
  const punchesAreCurrent =
    showWorkHours &&
    punchDayKey != null &&
    punchDayKey === getLocalDayKey(nowMs) &&
    firstInMs != null &&
    nowMs - firstInMs < 24 * 60 * 60 * 1000;
  const punchMs = punchesAreCurrent ? workedMsFromPunchRows(dayPunches, nowMs) : null;
  const workMs = punchMs != null ? punchMs : 0;
  const workHours = punchesAreCurrent && workMs > 0 ? formatDurationHms(workMs) : null;

  const showLastSession =
    !checkedIn &&
    lastSession?.inMs != null &&
    lastSession?.outMs != null &&
    isSameLocalDay(lastSession.inMs, Date.now());
  const lastInClock = showLastSession ? formatCheckInClock(lastSession.inMs) : null;
  const lastOutClock = showLastSession ? formatCheckInClock(lastSession.outMs) : null;
  const lastTotal = showLastSession
    ? formatDurationHms(Math.max(0, lastSession.outMs - lastSession.inMs))
    : null;

  // Real-time status from First IN / Last OUT punches (policy hours + late).
  // Do NOT call check-in API here — that creates punch spam and can reopen closed days.
  useEffect(() => {
    const effectiveIn = checkInAt ?? (punchesAreCurrent ? firstInMs : null) ?? (showLastSession ? lastSession?.inMs : null);
    const effectiveOut = checkedIn
      ? null
      : (checkOutAt ?? (punchesAreCurrent ? lastOutMs : null) ?? (showLastSession ? lastSession?.outMs : null));
    if (!employeeId || effectiveIn == null) {
      if (checkedIn) setLiveStatus("-");
      return;
    }
    const openSession = Boolean(checkedIn && effectiveOut == null);
    const status = computeLiveDayStatus({
      employeeId,
      checkInAt: effectiveIn,
      checkOutAt: openSession ? null : effectiveOut,
      openSession,
      workedHoursOverride: workMs > 0 ? workMs / 3600000 : undefined,
    });
    if (status && status !== "-") {
      setLiveStatus(status);
      if (openSession) lastSyncedStatusRef.current = status;
    } else if (lastSyncedStatusRef.current) {
      setLiveStatus(lastSyncedStatusRef.current);
    }
  }, [employeeId, checkedIn, checkInAt, checkOutAt, punchesAreCurrent, firstInMs, lastOutMs, workMs, showLastSession, lastSession, tick]);

  // Run attendance schedule / punch batch: refresh TODAY status without forcing checkout.
  useEffect(() => {
    const onRecalc = () => {
      const empId = employeeId;
      if (!empId) {
        setTick((n) => n + 1);
        return;
      }
      // Refresh from server instead of localStorage
      attendanceService.getCurrentAttendanceSession()
        .then(res => {
          if (res?.success) {
            const session = res.data;
            applyServerSession(session);
            setTick((n) => n + 1);
          }
        })
        .catch(() => {
          setTick((n) => n + 1);
        });
    };
    window.addEventListener(ATTENDANCE_SCHEDULE_RECALC_EVENT, onRecalc);
    window.addEventListener(ATTENDANCE_POLICY_UPDATED_EVENT, onRecalc);
    return () => {
      window.removeEventListener(ATTENDANCE_SCHEDULE_RECALC_EVENT, onRecalc);
      window.removeEventListener(ATTENDANCE_POLICY_UPDATED_EVENT, onRecalc);
    };
  }, [employeeId, applyServerSession]);

  useEffect(() => {
    if (!detailsOpen) return;
    updateDropdownPosition();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [detailsOpen, updateDropdownPosition]);

  useEffect(() => {
    if (!detailsOpen) return;
    const onMouseDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (e.target.closest("[data-portal-checkin-details]")) return;
      setDetailsOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [detailsOpen]);

  const handleCheckIn = async () => {
    if (punchBusy) {
      return;
    }
    setPunchBusy(true);
    const t = Date.now();
    if (!employeeId) {
      toast.error("No employee profile linked — cannot check in.");
      setPunchBusy(false);
      return;
    }
    await syncAttendancePoliciesCache();
    const gate = evaluateCheckInPunch({
      employee: { id: employeeId, employeeId },
      checkInMs: t,
      channel: "web",
      existingWorklog: { checkIn: checkInAt, checkOut: checkOutAt, open: checkedIn },
    });
    (gate.warnings || []).forEach((msg) => {
      if (gate.allowed === false) toast.error(msg);
      else toast(msg, { duration: 5000, icon: "⚠️" });
    });
    if (gate.allowed === false) {
      if (!(gate.warnings || []).length) {
        toast.error("Check-in is not allowed by your attendance policy.");
      }
      setPunchBusy(false);
      return;
    }
    let locationPayload = {};
    try {
      locationPayload = await resolvePunchLocationPayload(gate.policy, "web");
    } catch (locErr) {
      toast.error(locErr?.message || LOCATION_REQUIRED_MESSAGE);
      setPunchBusy(false);
      return;
    }
    
    // Backend is the source of truth - let it handle duplicate check-in detection
    // Do not use localStorage to determine if there's an open session
    const previousRow = { checkIn: checkInAt, checkOut: checkOutAt, open: checkedIn, openInAt };
    setCheckInAt(t);
    setOpenInAt(t);
    setCheckOutAt(null);
    setCheckedIn(true);
    lastSyncedStatusRef.current = null;
    setLiveStatus("-");

    try {
      const res = await attendanceService.checkIn({
        punchedAt: t,
        source: "web",
        ...locationPayload,
      });
      const serverStatus = res?.data?.status;
      if (serverStatus) {
        lastSyncedStatusRef.current = serverStatus;
        setLiveStatus(serverStatus);
      }
      const live = await attendanceService.getCurrentAttendanceSession();
      if (live?.success) applyServerSession(live.data);
    } catch (err) {
      setCheckedIn(Boolean(previousRow?.open));
      setCheckInAt(previousRow?.checkIn ?? null);
      setOpenInAt(previousRow?.openInAt ?? null);
      setCheckOutAt(previousRow?.open ? null : previousRow?.checkOut ?? null);
      const code = err?.response?.data?.code;
      if (code === "ALREADY_CHECKED_IN") {
        // Refresh from server to get the actual state
        attendanceService.getCurrentAttendanceSession()
          .then(res => {
            if (res?.success) {
              const session = res.data;
              applyServerSession(session);
            }
          })
          .catch(() => {});
      }
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Check-in was not saved to attendance. Please try again."
      );
    } finally {
      setPunchBusy(false);
    }
  };

  const handleCheckOut = async () => {
    if (punchBusy) {
      return;
    }
    setPunchBusy(true);
    const outMs = Date.now();
    let finalStatus = liveStatus;
    let firstIn = checkInAt;
    if (!employeeId) {
      toast.error("No employee profile linked — cannot check out.");
      setPunchBusy(false);
      return;
    }
    await syncAttendancePoliciesCache();
    
    // Backend is the source of truth - let it handle session validation
    // Do not use localStorage to determine if there's an open session
    const outGate = evaluateCheckInPunch({
      employee: { id: employeeId, employeeId },
      checkInMs: outMs,
      channel: "web",
      existingWorklog: { checkIn: checkInAt, checkOut: checkOutAt, open: checkedIn },
    });
    if (outGate.allowed === false && (outGate.warnings || []).length) {
      const webDenied = (outGate.warnings || []).find((w) =>
        String(w).toLowerCase().includes("web check-in is disabled")
      );
      if (webDenied) {
        toast.error("Web check-out is disabled for your attendance policy.");
        setPunchBusy(false);
        return;
      }
    }
    let locationPayload = {};
    try {
      locationPayload = await resolvePunchLocationPayload(outGate.policy, "web");
    } catch (locErr) {
      toast.error(locErr?.message || LOCATION_REQUIRED_MESSAGE);
      setPunchBusy(false);
      return;
    }
    
    const previousRow = { checkIn: checkInAt, checkOut: checkOutAt, open: checkedIn, openInAt };
    setCheckOutAt(outMs);
    setOpenInAt(null);
    setCheckedIn(false);

    try {
      const res = await attendanceService.checkOut({
        punchedAt: outMs,
        source: "web",
        ...locationPayload,
      });
      const serverStatus = res?.data?.status;
      if (serverStatus) {
        lastSyncedStatusRef.current = serverStatus;
        setLiveStatus(serverStatus);
      }
      const live = await attendanceService.getCurrentAttendanceSession();
      if (live?.success) applyServerSession(live.data);
    } catch (err) {
      // Revert on error
      setCheckedIn(Boolean(previousRow?.open));
      setCheckInAt(previousRow?.checkIn ?? null);
      setOpenInAt(previousRow?.openInAt ?? null);
      setCheckOutAt(previousRow?.open ? null : previousRow?.checkOut ?? null);
      
      const code = err?.response?.data?.code;
      const serverMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Check-out was not saved. You are still checked in.";
      toast.error(serverMsg);
      if (code === "ALREADY_CHECKED_OUT" || code === "PUNCH_PAIR_COMPLETE") {
        setCheckedIn(false);
        // Refresh from server to get actual state
        attendanceService.getCurrentAttendanceSession()
          .then(res => {
            if (res?.success) {
              const session = res.data;
              applyServerSession(session);
            }
          })
          .catch(() => {});
        return;
      }
      // For other errors, revert to previous state
      setCheckedIn(true);
      setCheckInAt(previousRow?.checkIn ?? null);
      setOpenInAt(previousRow?.openInAt ?? null);
      setCheckOutAt(null);
    }
  };

  const inMsForView = checkInAt;
  const outMsForView = checkedIn ? null : checkOutAt;

  const checkInClock = inMsForView != null ? formatCheckInClock(inMsForView) : null;
  const checkOutClock = outMsForView != null ? formatCheckInClock(outMsForView) : null;

  void tick;

  const hasDetails = inMsForView != null || showLastSession || (punchesAreCurrent && dayPunches.length > 0);
  const displayInClock =
    (punchesAreCurrent && firstInMs != null)
      ? formatCheckInClock(firstInMs)
      : (inMsForView != null ? checkInClock : lastInClock);
  const displayOutClock = checkedIn
    ? (inMsForView != null ? checkOutClock ?? "—" : "—")
    : (punchesAreCurrent && lastOutMs != null)
      ? formatCheckInClock(lastOutMs)
      : (inMsForView != null ? checkOutClock ?? "—" : lastOutClock ?? "—");
  const displayWorkHours =
    workHours ||
    (showLastSession ? lastTotal : null);

  const openDetails = () => {
    if (!hasDetails) return;
    updateDropdownPosition();
    setDetailsOpen(true);
  };

  const onTogglePunch = async () => {
    if (punchBusy || !apiReady) return;
    setPunchBusy(true);
    try {
      if (!checkedIn) {
        await handleCheckIn();
        setLastSession((prev) => {
          if (prev?.inMs && isSameLocalDay(prev.inMs, Date.now())) return prev;
          return null;
        });
      } else {
        await handleCheckOut();
      }
      if (hasDetails || checkInAt != null) {
        updateDropdownPosition();
        setDetailsOpen(true);
      }
    } finally {
      setPunchBusy(false);
    }
  };

  const detailsPanel =
    detailsOpen &&
    hasDetails &&
    createPortal(
      <div
        data-portal-checkin-details
        className={`fixed z-[var(--ds-z-dropdown)] w-[min(92vw,16rem)] rounded-md border shadow-xl opacity-100 ${
          darkMode
            ? "border-slate-600 bg-slate-900 text-gray-100"
            : "border-slate-200 bg-white text-gray-900"
        }`}
        style={{
          top: `${dropdownPosition.top}px`,
          right: `${dropdownPosition.right}px`,
        }}
      >
        <div className="flex items-center justify-between gap-2 border-b border-slate-200/80 px-2.5 py-2 dark:border-slate-700/80">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
            Today (First IN / Last OUT)
          </span>
          <button
            type="button"
            onClick={() => setDetailsOpen(false)}
            className={`rounded p-0.5 transition ${
              darkMode
                ? "text-slate-400 hover:bg-white/10 hover:text-slate-200"
                : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            }`}
            aria-label="Close check-in details"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="px-2.5 py-2">
          <div className="flex flex-wrap gap-1.5">
            <span className="inline-flex items-center rounded-full border border-emerald-200 dark:border-emerald-800 bg-emerald-50/70 dark:bg-emerald-950/30 px-2 py-0.5 text-[10px] font-semibold text-emerald-900 dark:text-emerald-200">
              In&nbsp;{displayInClock}
            </span>
            <span className="inline-flex items-center rounded-full border border-rose-200 dark:border-rose-800 bg-rose-50/70 dark:bg-rose-950/30 px-2 py-0.5 text-[10px] font-semibold text-rose-900 dark:text-rose-200">
              Out&nbsp;{displayOutClock ?? "—"}
            </span>
          </div>
          {displayWorkHours && (
            <p className="mt-1.5 text-[10px] tabular-nums text-gray-500 dark:text-gray-400">
              Work Hours&nbsp;{displayWorkHours}
            </p>
          )}
          <p className="mt-1 text-[10px] font-semibold text-slate-600 dark:text-slate-300">
            Status&nbsp;
            {liveStatus === "-"
              ? (checkedIn ? "In progress" : "Completed")
              : formatCompositeStatusLabel(liveStatus, attendanceMapping)}
          </p>
        </div>
      </div>,
      document.body
    );

  if (manualProcess) return null;

  return (
    <div
      className={`flex flex-col items-end gap-0.5 font-sans antialiased ${
        darkMode ? "text-gray-300" : "text-gray-800"
      }`}
    >
      <div className="relative flex items-center gap-1.5">
        <button
          ref={btnRef}
          type="button"
          onMouseEnter={openDetails}
          onClick={onTogglePunch}
          disabled={punchBusy || !apiReady}
          className={`inline-flex items-center gap-1.5 rounded-full border font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[hsl(var(--ds-bg))] ${
            isCompact ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm shadow-sm"
          } ${
            checkedIn
              ? darkMode
                ? "border-rose-500/70 bg-rose-950/40 text-rose-200 hover:bg-rose-950/60"
                : "border-rose-500 bg-rose-50 text-rose-800 hover:bg-rose-100"
              : darkMode
              ? "border-emerald-500/70 bg-emerald-950/40 text-emerald-200 hover:bg-emerald-950/60"
              : "border-emerald-600 bg-emerald-50 text-emerald-900 hover:bg-emerald-100"
          } ${punchBusy || !apiReady ? "opacity-60 cursor-wait" : ""}`}
          aria-label={checkedIn ? "Check out" : "Check in"}
          aria-expanded={detailsOpen}
        >
          <ClockIcon
            className={isCompact ? "h-3.5 w-3.5 shrink-0" : "h-4 w-4 shrink-0"}
            aria-hidden
          />
          {checkedIn ? "Check Out" : "Check In"}
        </button>
        {detailsPanel}
      </div>
      {workHours && (
        <span
          className={`inline-flex items-center rounded-full border border-gray-200/90 dark:border-[#3C3C4C] bg-white/50 dark:bg-[#2a2a2a]/80 px-2 py-0.5 tabular-nums font-semibold text-gray-800 dark:text-gray-100 ${
            isCompact ? "text-[10px]" : "text-xs"
          }`}
        >
          Work Hours&nbsp;{workHours}
        </span>
      )}
    </div>
  );
}

export { ATTENDANCE_PUNCH_UPDATED_EVENT };
